import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { wabaApi } from "../../lib/wabaApi";
import { useClickOutside } from "../../lib/useClickOutside";
import { SendBehaviorToggles } from "./SendBehaviorToggles";
import { ProfilePicture } from "./ProfilePicture";
import { EmbeddedSignup } from "./EmbeddedSignup";
import { InstagramSignup } from "./InstagramSignup";
import { MessengerSignup } from "./MessengerSignup";
import { CompleteSignup } from "./CompleteSignup";
import { AdsSignup } from "./AdsSignup";
import { ClassificadoConnect, IntegracaoLog, MeliDiagnostico, OlxLeadsConfig } from "./ClassificadoConnect";
import { IgCommentReply } from "./IgCommentReply";
import { OutboundWebhooks } from "./OutboundWebhooks";
import { ExternalConnection } from "./ExternalConnection";
import { DialogPanel } from "../shared/DialogPanel";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { CallConsole } from "./CallConsole";
import { MessageLog } from "./MessageLog";
import { LeadSearch } from "./LeadSearch";
import { TemplateManager } from "./TemplateManager";
import { PhoneIcon, AlertTriangleIcon, UsersIcon, TransferIcon, XIcon } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";

/**
 * Um canal dentro do card. Conectado mostra as configurações dele; desconectado
 * mostra o botão de conectar. Os dois convivem — a conexão é do cliente.
 */
function ChannelSection({
  nome, conectado, detalhe, bm, saude, aberto, onToggle, children,
}: {
  nome: string;
  conectado: boolean;
  // Saúde do canal no cabeçalho (confirmada no provedor ou derivada do banco); null = só a bolinha.
  saude?: { texto: string; cor: string } | null;
  detalhe?: string | null;
  bm?: string | null;
  aberto: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--line)",
        borderRadius: 10,
        background: conectado ? "transparent" : "var(--surface-input)",
      }}
    >
      <button
        onClick={onToggle}
        aria-expanded={aberto}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
      >
        <span
          aria-hidden
          style={{
            width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
            background: conectado ? "var(--success)" : "var(--line-strong)",
          }}
        />
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>{nome}</span>
        {detalhe && (
          <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{detalhe}</span>
        )}
        {bm && (
          <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>· BM: {bm}</span>
        )}
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {saude && (
            <span style={{ fontSize: 11, fontWeight: 600, color: saude.cor }}>{saude.texto}</span>
          )}
          {!conectado && (
            <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>não conectado</span>
          )}
          <span aria-hidden style={{ fontSize: 10, color: "var(--ink-muted)" }}>{aberto ? "▾" : "▸"}</span>
        </span>
      </button>
      {aberto && <div style={{ padding: "0 14px 12px" }}>{children}</div>}
    </div>
  );
}

interface Connection {
  id: string;
  name: string;
  instance_id: string;
  phone_number_id?: string | null;
  waba_id?: string | null;
  page_id?: string | null;
  waba_business_name?: string | null;
  page_business_name?: string | null;
  claimed_by_agent_id?: string | null;
  claimed_by_agent_name?: string | null;
  ig_user_id?: string | null;
  ig_username?: string | null;
  messenger_active?: boolean;
  page_name?: string | null;
  ig_comment_reply?: string | null;
  ads_connected?: boolean;
  meli_user_id?: string | null;
  meli_nickname?: string | null;
  meli_status?: string | null;   // connected | reconnect
  olx_account_email?: string | null;
  olx_account_name?: string | null;
  olx_status?: string | null;    // connected | reconnect
  webmotors_user?: string | null;        // e-mail do Integrador de API
  webmotors_status?: string | null;      // connected | error | reconnect
  webmotors_cnpj?: string | null;        // loja: vem no 1º lead
  webmotors_cliente_id?: string | null;
  webmotors_ok_at?: string | null;       // última chamada que deu certo (UTC)
  webmotors_error?: string | null;
  email_address?: string | null;         // <token>@mail.revendai.com (leads de portais por e-mail)
  status: string;
  previous_status: string | null;
  owner_name: string | null;
  phone_number: string | null;
  display_name: string | null;
  quality_rating: string | null;
  messaging_limit: string | null;
  category: string | null;
  description: string | null;
  website: string | null;
  auto_split_text: boolean;
  auto_process_links: boolean;
  auto_carousel?: boolean;
  burst_carousel?: boolean;
  burst_buttons?: string[] | null;
  auto_patioo_cta?: boolean;
  buttons: string[] | null;
  typing_indicator: boolean;
  typing_delay_seconds: number;
  auto_mark_read: boolean;
  send_via_bsuid?: boolean;
  profile_picture_url: string | null;
  meta_disconnected_at?: string | null;
  participants: { user_id: string; user_name: string }[];
  // Conexão externa (sem Meta): entrada por POST /inbound, saída pelo webhook.
  external?: boolean;
  external_webhook_url?: string | null;
}

const QUALITY_COLOR: Record<string, string> = { GREEN: "var(--ok)", YELLOW: "var(--warn)", RED: "var(--danger)" };
const QUALITY_LABEL: Record<string, string> = { GREEN: "Alta qualidade", YELLOW: "Média qualidade", RED: "Baixa qualidade" };

type WabaAccessMode = null | "choose" | "participants" | "transfer-search" | "transfer-confirm";

interface Props {
  conn: Connection;
  onChanged: () => void;
  initialLog?: string | null;  // canal: abre o Log de Mensagens já filtrado (deep-link ?open=&log=)
  canDelete?: boolean;
  canManageParticipants?: boolean;
  canChangeOwner?: boolean;
  canCreateTemplate?: boolean;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      style={{ background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 5, color: copied ? "var(--ok)" : "var(--ink-secondary)", fontSize: 11, padding: "3px 8px", cursor: "pointer" }}
    >
      {copied ? "Copiado" : "Copiar"}
    </button>
  );
}

export function ConnectionCard({ conn, onChanged, initialLog = null, canDelete = true, canManageParticipants = true, canChangeOwner = false, canCreateTemplate = true }: Props) {
  const qc = useQueryClient();
  const [revealed, setRevealed] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(conn.name);
  const renameMut = useMutation({
    mutationFn: () => wabaApi.patch(`/connections/${conn.id}`, { name: nameDraft.trim() }),
    onSuccess: () => { setRenaming(false); onChanged(); },
  });
  const [showWebhooks, setShowWebhooks] = useState(false);
  // Canais colapsáveis — sempre nascem todos recolhidos.
  const [openChannels, setOpenChannels] = useState<Record<string, boolean>>({});
  // Webmotors: vermelho se a Webmotors recusou (status) ou sem chamada que dê certo há 3 h
  // (a consulta de reserva roda 1x/h por loja; 3 falhas seguidas).
  const wmOkAt = conn.webmotors_ok_at ? Date.parse(/Z|[+-]\d\d:\d\d$/.test(conn.webmotors_ok_at)
    ? conn.webmotors_ok_at : conn.webmotors_ok_at + "Z") : 0;
  const wmOk = conn.webmotors_status === "connected" && Date.now() - wmOkAt < 3 * 3600 * 1000;
  // Primeiro canal conectado nesta ordem abre sozinho; os demais ficam colapsados até o clique.
  const conectados: Record<string, boolean> = {
    whatsapp: !!conn.phone_number_id && !conn.meta_disconnected_at,
    instagram: !!conn.ig_user_id,
    messenger: !!conn.messenger_active,
    email: !!conn.email_address,
    olx: conn.olx_status === "connected",
    meli: conn.meli_status === "connected",
    webmotors: wmOk,
    ads: !!conn.ads_connected,
  };
  const firstOpen = ["whatsapp", "instagram", "messenger", "email", "olx", "meli", "webmotors", "ads"].find(k => conectados[k]);
  const isOpen = (k: string) => openChannels[k] ?? k === firstOpen;
  // Inverte o valor efetivo: o canal aberto por padrão fecha no primeiro clique.
  const toggleChannel = (k: string) => setOpenChannels(s => ({ ...s, [k]: !(s[k] ?? k === firstOpen) }));
  const spTime = (t: number) => new Date(t).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo",
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  const [confirmUnbind, setConfirmUnbind] = useState(false);
  const unbindMut = useMutation({
    // Funciona mesmo com agente já deletado (destrava WABA fantasma).
    mutationFn: () => wabaApi.delete(`/bindings/agent/${conn.claimed_by_agent_id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["waba-connections"] }); onChanged(); },
  });
  const [showLog, setShowLog] = useState(!!initialLog);
  const [showCalls, setShowCalls] = useState(false);
  const [showLeads, setShowLeads] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleteStep, setDeleteStep] = useState<0 | 1 | 2 | 3>(0);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [accessMode, setAccessMode] = useState<WabaAccessMode>(null);
  const [confirmRemoveUserId, setConfirmRemoveUserId] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [transferSearch, setTransferSearch] = useState("");
  const [transferTarget, setTransferTarget] = useState<{ user_id: string; user_name: string } | null>(null);
  const userSearchRef = useRef<HTMLDivElement>(null);
  const transferSearchRef = useRef<HTMLDivElement>(null);
  useClickOutside(userSearchRef, userSearch.length >= 2, () => setUserSearch(""));
  useClickOutside(transferSearchRef, transferSearch.length >= 2 && !transferTarget, () => setTransferSearch(""));

  const { data: webhooks = [] } = useQuery<any[]>({
    queryKey: ["waba-outbound-webhooks", conn.id],
    queryFn: () => wabaApi.get(`/connections/${conn.id}/outbound-webhooks`),
    enabled: conn.status === "active",
  });

  // Confirmação real na Meta: app inscrito nos webhooks + número registrado na
  // Cloud API (o connect não bloqueia quando falham — coex às vezes dá timeout).
  // null = não deu pra checar aquele item.
  const { data: metaStatus, isFetching: metaChecking } = useQuery<{
    subscribed: boolean | null;
    registered: boolean | null;
    // true = coexistência (app WhatsApp Business + API), false = API pura, null = Meta não informou.
    coexistence: boolean | null;
    phone_status?: string;
    platform_type?: string;
    payment_method: boolean | null;
    can_send: "AVAILABLE" | "LIMITED" | "BLOCKED" | null;
    health_errors?: string[];
    // Franquia mensal de service messages (pricing_analytics, aproximado).
    free_tier?: { free_tier_limit: number; free_used: number; free_remaining: number; billable: number } | null;
  }>({
    queryKey: ["waba-meta-status", conn.id],
    queryFn: () => wabaApi.get(`/connections/${conn.id}/meta-status`),
    // Sem WABA (conexão externa ou só IG/Messenger/portais) a rota responde 400.
    enabled: conn.status === "active" && !!conn.waba_id,
    staleTime: 60_000,
    retry: false,
  });
  // Instagram/Messenger: a Página segue inscrita nos webhooks? (mesma Página pros dois canais)
  const { data: igStatus, isFetching: igChecking } = useQuery<{ subscribed: boolean | null }>({
    queryKey: ["waba-ig-status", conn.id],
    queryFn: () => wabaApi.get(`/connections/${conn.id}/ig-status`),
    enabled: conn.status === "active" && !!conn.page_id && (!!conn.ig_user_id || !!conn.messenger_active),
    staleTime: 60_000,
    retry: false,
  });
  const igResubMut = useMutation({
    mutationFn: () => wabaApi.post(`/connections/${conn.id}/subscribe?channel=instagram`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["waba-ig-status", conn.id] }),
  });
  // Fail-closed como no WhatsApp: só confirmação positiva fica verde.
  const igSaude = igStatus == null ? null
    : igStatus.subscribed === true ? { texto: "Webhooks ✓", cor: "var(--ok)" }
    : { texto: igStatus.subscribed === false ? "Desinscrito" : "Não confirmado", cor: "var(--danger)" };
  const igResubBox = igStatus != null && igStatus.subscribed !== true && (
    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--warn-soft)", border: "1px solid var(--warn-line)", borderRadius: 8, padding: "8px 12px" }}>
      <span style={{ fontSize: 12, color: "var(--warn)", flex: 1 }}>
        <AlertTriangleIcon size={12} /> Página não inscrita nos webhooks da Meta — as mensagens não chegam ao agente.
        {igResubMut.isError && <em style={{ color: "var(--danger)", fontStyle: "normal" }}> Retry falhou, tente novamente.</em>}
      </span>
      <button
        onClick={() => igResubMut.mutate()}
        disabled={igResubMut.isPending || igChecking}
        style={{ background: "var(--warn)", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, padding: "5px 12px", cursor: "pointer", opacity: igResubMut.isPending || igChecking ? 0.6 : 1 }}
      >
        {igResubMut.isPending ? "Inscrevendo…" : igChecking ? "Conferindo…" : "Reinscrever"}
      </button>
    </div>
  );
  // E-mail de portais: último recebido. Sem nada há 24 h = alerta pra conferir o
  // encaminhamento no portal; some sozinho no próximo e-mail.
  const { data: emailStatus } = useQuery<{ last_at: string | null }>({
    queryKey: ["waba-email-status", conn.id],
    queryFn: () => wabaApi.get(`/connections/${conn.id}/email-status`),
    enabled: conn.status === "active" && !!conn.email_address,
    staleTime: 60_000,
    retry: false,
  });
  const emailSaude = (() => {
    if (!emailStatus) return null;
    if (!emailStatus.last_at) return { texto: "aguardando o primeiro e-mail", cor: "var(--ink-muted)" };
    const t = emailStatus.last_at;
    const horas = Math.floor((Date.now() - Date.parse(/Z|[+-]\d\d:\d\d$/.test(t) ? t : t + "Z")) / 3600_000);
    return horas >= 24 ? { texto: `sem e-mail há ${horas} h, confira a conexão`, cor: "var(--warn)" } : null;
  })();
  // Botão "Dados da WABA": GET {waba_id}?fields=... na Graph API, JSON cru.
  const wabaAccountMut = useMutation({
    mutationFn: () => wabaApi.get<Record<string, unknown>>(`/connections/${conn.id}/waba-account`),
  });
  const resubMut = useMutation({
    mutationFn: () => wabaApi.post(`/connections/${conn.id}/subscribe`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["waba-meta-status", conn.id] }),
  });
  const registerMut = useMutation({
    mutationFn: () => wabaApi.post(`/connections/${conn.id}/register`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["waba-meta-status", conn.id] }),
  });

  const { data: userSearchResults = [] } = useQuery<{ user_id: string; user_name: string }[]>({
    queryKey: ["waba-user-search", userSearch],
    queryFn: () => wabaApi.get(`/users/search?q=${encodeURIComponent(userSearch)}`),
    enabled: userSearch.length >= 2,
    staleTime: 10_000,
  });

  const { data: transferSearchResults = [] } = useQuery<{ user_id: string; user_name: string }[]>({
    queryKey: ["waba-transfer-search", transferSearch],
    queryFn: () => wabaApi.get(`/users/search?q=${encodeURIComponent(transferSearch)}`),
    enabled: transferSearch.length >= 2,
    staleTime: 10_000,
  });

  const transferOwnerMut = useMutation({
    mutationFn: (userId: string) => wabaApi.patch(`/connections/${conn.id}/transfer-owner`, { user_id: userId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["waba-connections"] });
      onChanged();
      setAccessMode(null);
      setTransferSearch("");
      setTransferTarget(null);
    },
  });

  const addParticipantMut = useMutation({
    mutationFn: (userId: string) => wabaApi.post(`/connections/${conn.id}/participants`, { user_id: userId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["waba-connections"] });
      onChanged();
      setUserSearch("");
    },
  });

  const removeParticipantMut = useMutation({
    mutationFn: (userId: string) => wabaApi.delete(`/connections/${conn.id}/participants/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["waba-connections"] });
      onChanged();
      setConfirmRemoveUserId(null);
    },
  });

  // Troca a chave: a antiga morre na hora e quebra as integrações do cliente.
  // Dois avisos de propósito (decisão 2026-10-06).
  async function handleRotateKey() {
    if (!confirm(
      "⚠️ ATENÇÃO — ISSO VAI QUEBRAR AS INTEGRAÇÕES DESTA CONEXÃO\n\n" +
      "Gerar uma nova API Key DESLIGA a chave atual NA HORA.\n\n" +
      "Todo sistema que usa esta conexão (CRM, n8n, integrações do cliente) vai " +
      "PARAR de enviar e receber mensagens até ser atualizado com a chave nova.\n\n" +
      "Deseja continuar?",
    )) return;
    if (!confirm(
      `🚨 ÚLTIMA CONFIRMAÇÃO — "${conn.name}"\n\n` +
      "NÃO TEM COMO DESFAZER: a chave antiga morre agora e NÃO volta.\n\n" +
      "Só confirme se quem integra com esta conexão já está avisado para trocar a " +
      "chave logo em seguida. Se tiver dúvida, CANCELE.\n\n" +
      "Gerar a nova API Key agora?",
    )) return;
    try {
      const data = await wabaApi.post<{ api_key: string }>(`/connections/${conn.id}/rotate-key`);
      setApiKey(data.api_key);
      setRevealed(true);
    } catch (err: any) {
      alert(err?.payload?.detail ?? "Não foi possível gerar a nova chave. A chave atual continua valendo.");
    }
  }

  async function handleReveal() {
    if (revealed) { setRevealed(false); return; }
    const data = await wabaApi.get<{ api_key: string }>(`/connections/${conn.id}/api-key`);
    setApiKey(data.api_key);
    setRevealed(true);
  }

  async function handleRefresh() {
    setRefreshing(true);
    try {
      // POST devolve só esta conexão: troca o item no cache da lista (sem rebaixar
      // todas) e refaz os checks da Meta deste card.
      const updated = await wabaApi.post<any>(`/connections/${conn.id}/refresh`);
      qc.setQueryData<any[]>(["waba-connections"], (list) => list?.map((c) => (c.id === conn.id ? { ...c, ...updated } : c)));
      qc.invalidateQueries({ queryKey: ["waba-meta-status", conn.id] });
    } catch (err: any) {
      alert(err?.message || "Não foi possível atualizar a saúde da conexão agora.");
    }
    finally { setRefreshing(false); }
  }

  async function handleToggleStatus() {
    setToggling(true);
    try {
      const action = conn.status === "disabled" ? "reactivate" : "disable";
      await wabaApi.post(`/connections/${conn.id}/${action}`);
      qc.invalidateQueries({ queryKey: ["waba-connections"] });
      onChanged();
    } finally {
      setToggling(false);
    }
  }

  async function handleDisconnect() {
    setDeleting(true);
    try {
      await wabaApi.delete(`/connections/${conn.id}`);
      onChanged();
    } finally {
      setDeleting(false);
      setDeleteStep(0);
    }
  }

  async function handleExportLeads() {
    setExporting(true);
    try {
      await wabaApi.download(`/connections/${conn.id}/leads/export`, `leads_${conn.name}.csv`);
    } finally {
      setExporting(false);
    }
  }

  const qualityColor = QUALITY_COLOR[conn.quality_rating ?? ""] ?? "var(--ink-muted)";
  const qualityLabel = QUALITY_LABEL[conn.quality_rating ?? ""] ?? "—";

  const cardStyle: React.CSSProperties = {
    background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12,
    padding: 20, maxWidth: 560,
  };
  const labelStyle: React.CSSProperties = { fontSize: 11, color: "var(--ink-muted)" };
  const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 };
  const codeStyle: React.CSSProperties = {
    flex: 1, fontFamily: "var(--font-mono)", fontSize: 12, padding: "4px 8px",
    background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 5, color: "var(--ink-soft)",
  };

  const modalBase: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any };
  const modalBox: React.CSSProperties = { background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 12, padding: 28, maxWidth: 400, width: "90%" };

  return (
    <>
      {/* Passo 1 — confirmação inicial */}
      {deleteStep === 1 && (
        <div {...backdropClose(() => { setDeleteStep(0); })} style={modalBase}>
          <DialogPanel onClose={() => setDeleteStep(0)} role="alertdialog" label="Remover conexão?" style={modalBox}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", marginBottom: 10 }}>Remover conexão?</div>
            <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: "0 0 20px" }}>
              Deseja remover a conexão <strong style={{ color: "var(--ink)" }}>{conn.name}</strong>?
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setDeleteStep(2)} style={{ flex: 1, background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>
                Avançar
              </button>
              <button onClick={() => setDeleteStep(0)} style={{ flex: 1, background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>
                Cancelar
              </button>
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Passo 2 — confirmação irreversível */}
      {deleteStep === 2 && (
        <div {...backdropClose(() => { setDeleteStep(0); })} style={modalBase}>
          <DialogPanel onClose={() => setDeleteStep(0)} role="alertdialog" label="Ação irreversível" style={modalBox}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--danger)", marginBottom: 10 }}>Ação irreversível</div>
            <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: "0 0 20px" }}>
              Essa ação é irreversível e resultará na exclusão total da conexão. Deseja continuar mesmo assim?
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setDeleteStep(3)} style={{ flex: 1, background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>
                Avançar
              </button>
              <button onClick={() => setDeleteStep(0)} style={{ flex: 1, background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>
                Cancelar
              </button>
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Passo 3 — backup dos leads antes de excluir */}
      {deleteStep === 3 && (
        <div {...backdropClose(() => { if (!deleting) { setDeleteStep(0); }; })} style={modalBase}>
          <DialogPanel onClose={() => { if (!deleting) setDeleteStep(0); }} role="alertdialog" label="Baixe os leads antes de excluir" style={modalBox}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", marginBottom: 10 }}>Baixe os leads antes de excluir</div>
            <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: "0 0 16px" }}>
              Os leads desta conexão (telefone, bsuid e ctwa_clid) serão apagados para sempre junto com a WABA. Baixe a planilha antes — não há como recuperar depois.
            </p>
            <button onClick={handleExportLeads} disabled={exporting} style={{ width: "100%", background: "var(--surface-input)", color: "var(--ink)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "10px 0", fontSize: 13, cursor: "pointer", marginBottom: 16 }}>
              {exporting ? "Gerando planilha..." : "⬇ Baixar planilha de leads"}
            </button>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleDisconnect} disabled={deleting} style={{ flex: 1, background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>
                {deleting ? "Excluindo..." : "Excluir definitivamente"}
              </button>
              <button onClick={() => setDeleteStep(0)} disabled={deleting} style={{ flex: 1, background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>
                Cancelar
              </button>
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Unified access popup */}
      {accessMode !== null && (
        <div {...backdropClose(() => { setAccessMode(null); setConfirmRemoveUserId(null); setUserSearch(""); setTransferSearch(""); setTransferTarget(null); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => { setAccessMode(null); setConfirmRemoveUserId(null); setUserSearch(""); setTransferSearch(""); setTransferTarget(null); }} label="Gerenciar acessos" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 380, display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>
                {accessMode === "choose" ? "Gerenciar Acessos" :
                 accessMode === "participants" ? "Adicionar participantes" :
                 accessMode === "transfer-search" ? "Trocar proprietário" :
                 "Confirmar transferência"}
              </span>
              <button
                onClick={() => { setAccessMode(null); setConfirmRemoveUserId(null); setUserSearch(""); setTransferSearch(""); setTransferTarget(null); }}
                style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
              >×</button>
            </div>

            {/* Choose screen */}
            {accessMode === "choose" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {canManageParticipants && (
                  <button
                    onClick={() => setAccessMode("participants")}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, color: "var(--ink)", fontSize: 13, cursor: "pointer", textAlign: "left", width: "100%" }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--brand)")}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--line)")}
                  >
                    <span style={{ display: "flex", color: "var(--ink)" }}><UsersIcon size={18} /></span>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Adicionar participantes</div>
                      <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Dá acesso a outros gestores nesta conexão</div>
                    </div>
                  </button>
                )}
                {canChangeOwner && (
                  <button
                    onClick={() => setAccessMode("transfer-search")}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, color: "var(--ink)", fontSize: 13, cursor: "pointer", textAlign: "left", width: "100%" }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--brand)")}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--line)")}
                  >
                    <span style={{ display: "flex", color: "var(--ink)" }}><TransferIcon size={18} /></span>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Trocar proprietário</div>
                      <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Transfere a propriedade desta conexão para outro gestor</div>
                    </div>
                  </button>
                )}
              </div>
            )}

            {/* Participants screen */}
            {accessMode === "participants" && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", borderRadius: 8, background: "var(--danger-soft)" }}>
                    <span style={{ fontSize: 13, color: "var(--danger)" }}>{conn.owner_name ?? "—"}</span>
                    <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>owner</span>
                  </div>
                  {(conn.participants ?? []).map(p => (
                    <div key={p.user_id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", borderRadius: 8, background: "var(--warn-soft)" }}>
                        <span style={{ fontSize: 13, color: "var(--warn)" }}>{p.user_name}</span>
                        <button onClick={() => setConfirmRemoveUserId(confirmRemoveUserId === p.user_id ? null : p.user_id)} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
                      </div>
                      {confirmRemoveUserId === p.user_id && (
                        <div style={{ padding: "8px 10px", background: "var(--surface-card)", borderRadius: 8, border: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 8 }}>
                          <span style={{ fontSize: 12, color: "var(--ink-mid)" }}>Remover <strong style={{ color: "var(--ink)" }}>{p.user_name}</strong> dos participantes?</span>
                          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={() => setConfirmRemoveUserId(null)} style={{ padding: "4px 12px", background: "var(--line)", border: "none", borderRadius: 6, color: "var(--ink-secondary)", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
                            <button onClick={() => removeParticipantMut.mutate(p.user_id)} disabled={removeParticipantMut.isPending} style={{ padding: "4px 12px", background: "var(--danger-soft)", border: "none", borderRadius: 6, color: "var(--danger)", fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: removeParticipantMut.isPending ? 0.6 : 1 }}>Remover</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ borderTop: "1px solid var(--line)" }} />

                <div ref={userSearchRef} style={{ position: "relative" }}>
                  <input
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="Buscar gestor por nome..."
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "var(--surface-raised)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
                  />
                  {userSearchResults.length > 0 && userSearch.length >= 2 && (
                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", zIndex: 10 }}>
                      {userSearchResults
                        .filter(u => !(conn.participants ?? []).some(p => p.user_id === u.user_id))
                        .map(u => (
                          <button key={u.user_id} onClick={() => addParticipantMut.mutate(u.user_id)}
                            style={{ width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", color: "var(--ink)", fontSize: 13, cursor: "pointer" }}
                            onMouseEnter={e => (e.currentTarget.style.background = "var(--line)")}
                            onMouseLeave={e => (e.currentTarget.style.background = "none")}
                          >{u.user_name}</button>
                        ))}
                    </div>
                  )}
                </div>

                {canChangeOwner && (
                  <button onClick={() => { setAccessMode("choose"); setConfirmRemoveUserId(null); setUserSearch(""); }} style={{ alignSelf: "flex-start", background: "none", border: "none", color: "var(--ink-muted)", fontSize: 12, cursor: "pointer", padding: 0 }}>
                    ← Voltar
                  </button>
                )}
              </>
            )}

            {/* Transfer search screen */}
            {accessMode === "transfer-search" && (
              <>
                <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.5 }}>
                  Proprietário atual: <strong style={{ color: "var(--danger)" }}>{conn.owner_name ?? "—"}</strong>
                </div>
                <div ref={transferSearchRef} style={{ position: "relative" }}>
                  <input
                    autoFocus
                    value={transferSearch}
                    onChange={e => { setTransferSearch(e.target.value); setTransferTarget(null); }}
                    placeholder="Buscar novo proprietário..."
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "var(--surface-raised)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
                  />
                  {transferSearchResults.length > 0 && !transferTarget && (
                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", zIndex: 10 }}>
                      {transferSearchResults.map(u => (
                        <button key={u.user_id} onClick={() => { setTransferTarget(u); setTransferSearch(u.user_name); }}
                          style={{ width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", color: "var(--ink)", fontSize: 13, cursor: "pointer" }}
                          onMouseEnter={e => (e.currentTarget.style.background = "var(--line)")}
                          onMouseLeave={e => (e.currentTarget.style.background = "none")}
                        >{u.user_name}</button>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  {canManageParticipants ? (
                    <button onClick={() => { setAccessMode("choose"); setTransferSearch(""); setTransferTarget(null); }} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Voltar</button>
                  ) : (
                    <button onClick={() => setAccessMode(null)} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
                  )}
                  <button
                    onClick={() => setAccessMode("transfer-confirm")}
                    disabled={!transferTarget}
                    style={{ padding: "7px 16px", background: transferTarget ? "var(--brand)" : "var(--line)", border: "none", borderRadius: 8, color: transferTarget ? "#fff" : "var(--ink-dim)", fontSize: 13, fontWeight: 600, cursor: transferTarget ? "pointer" : "default" }}
                  >Continuar</button>
                </div>
              </>
            )}

            {/* Transfer confirm screen */}
            {accessMode === "transfer-confirm" && transferTarget && (
              <>
                <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.6 }}>
                  O proprietário da conexão <strong style={{ color: "var(--ink)" }}>{conn.name}</strong> será trocado de{" "}
                  <strong style={{ color: "var(--danger)" }}>{conn.owner_name ?? "—"}</strong> para{" "}
                  <strong style={{ color: "var(--ok)" }}>{transferTarget.user_name}</strong>.
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button onClick={() => setAccessMode("transfer-search")} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Voltar</button>
                  <button
                    onClick={() => transferOwnerMut.mutate(transferTarget.user_id)}
                    disabled={transferOwnerMut.isPending}
                    style={{ padding: "7px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: transferOwnerMut.isPending ? 0.6 : 1 }}
                  >
                    {transferOwnerMut.isPending ? "Transferindo..." : "Confirmar troca"}
                  </button>
                </div>
                {transferOwnerMut.isError && (
                  <span style={{ fontSize: 12, color: "var(--danger)" }}>
                    {(transferOwnerMut.error as any)?.payload?.detail ?? "Erro ao transferir. Tente novamente."}
                  </span>
                )}
              </>
            )}

          </DialogPanel>
        </div>
      )}

    <div style={cardStyle}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        {conn.status === "active" && <ProfilePicture conn={conn} onChanged={onChanged} />}
        <div style={{ flex: 1 }}>
          {renaming ? (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                autoFocus
                value={nameDraft}
                maxLength={128}
                onChange={e => setNameDraft(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && nameDraft.trim()) renameMut.mutate(); if (e.key === "Escape") { setRenaming(false); setNameDraft(conn.name); } }}
                style={{ fontSize: 14, fontWeight: 600, padding: "3px 8px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 6, color: "var(--ink)", outline: "none", minWidth: 180 }}
              />
              <button onClick={() => renameMut.mutate()} disabled={!nameDraft.trim() || renameMut.isPending} style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>
                {renameMut.isPending ? "..." : "Salvar"}
              </button>
              <button onClick={() => { setRenaming(false); setNameDraft(conn.name); }} style={{ background: "transparent", border: "none", color: "var(--ink-muted)", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
            </span>
          ) : (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>{conn.name}</span>
              <button onClick={() => { setNameDraft(conn.name); setRenaming(true); }} title="Renomear" style={{ background: "transparent", border: "none", color: "var(--ink-dim)", fontSize: 12, cursor: "pointer", padding: 0 }}>✎</button>
            </span>
          )}
          {conn.phone_number && (
            <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}><PhoneIcon size={12} /> {conn.phone_number}</div>
          )}
        </div>
        <span style={{
          fontSize: 11, padding: "2px 9px", borderRadius: 99,
          background: conn.status === "active" ? "var(--ok-soft)" : conn.status === "disabled" ? "var(--surface-card)" : "var(--surface-input)",
          color: conn.status === "active" ? "var(--ok)" : conn.status === "disabled" ? "var(--ink-muted)" : "var(--warn)",
          border: `1px solid ${conn.status === "active" ? "var(--ok-line)" : conn.status === "disabled" ? "var(--line-strong)" : "var(--warn-line)"}`,
        }}>
          {conn.status === "active" ? "Ativo" : conn.status === "disabled" ? "Desativado" : "Aguardando conexão"}
        </span>
        {conn.status === "active" && (
          <button onClick={handleRefresh} disabled={refreshing} title="Atualizar saúde" style={{ background: "transparent", border: "1px solid var(--line)", borderRadius: 5, color: "var(--ink-muted)", fontSize: 14, padding: "3px 7px", cursor: "pointer" }}>
            {refreshing ? "⟳" : "↻"}
          </button>
        )}
        {conn.status === "active" && (
          <button onClick={handleToggleStatus} disabled={toggling} style={{ background: "transparent", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-muted)", fontSize: 12, padding: "5px 12px", cursor: "pointer" }}>
            {toggling ? "Desativando..." : "Desativar"}
          </button>
        )}
        {conn.status === "disabled" && (
          <button onClick={handleToggleStatus} disabled={toggling} style={{ background: "var(--ok-soft)", border: "1px solid var(--ok-line)", borderRadius: 8, color: "var(--ok)", fontSize: 12, padding: "5px 12px", cursor: "pointer", fontWeight: 600 }}>
            {toggling ? "Reativando..." : "Reativar"}
          </button>
        )}
        {canDelete && (
          <button onClick={() => setDeleteStep(1)} style={{ background: "transparent", border: "1px solid var(--brand-line)", borderRadius: 8, color: "var(--brand)", fontSize: 12, padding: "5px 12px", cursor: "pointer" }}>
            Remover
          </button>
        )}
      </div>

      {/* Métricas de saúde do WhatsApp (só com WABA vinculada) */}
      {conn.status === "active" && !!conn.waba_id && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: "var(--line)", borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
          {[
            // Fail-closed: só confirmação POSITIVA da Meta fica verde. null
            // (check falhou — token/id morto) é tratado como desconectado,
            // nunca como "—" neutro que mascara conexão quebrada.
            {
              label: "Registro (Meta)",
              value: metaStatus == null ? "Verificando…" : metaStatus.registered === true
                ? `Registrado ✓${metaStatus.coexistence === true ? " · Coexistência" : metaStatus.coexistence === false ? " · API pura" : ""}`
                : "Desconectado",
              color: metaStatus == null ? "var(--ink-muted)" : metaStatus.registered === true ? "var(--ok)" : "var(--danger)",
            },
            { label: "Limite (24h)", value: conn.messaging_limit ?? "Indisponível", color: "var(--ink)" },
            { label: "Qualidade", value: qualityLabel, color: qualityColor },
            {
              label: "Webhooks (Meta)",
              value: metaStatus == null ? "Verificando…" : metaStatus.subscribed === true ? "Inscritos ✓" : "Desconectado",
              color: metaStatus == null ? "var(--ink-muted)" : metaStatus.subscribed === true ? "var(--ok)" : "var(--danger)",
            },
            {
              label: "Forma de pagamento (Meta)",
              value: metaStatus == null ? "Verificando…" : metaStatus.payment_method === true ? "OK ✓" : metaStatus.payment_method === false ? "Com problema" : "Indisponível",
              color: metaStatus == null ? "var(--ink-muted)" : metaStatus.payment_method === true ? "var(--ok)" : metaStatus.payment_method === false ? "var(--danger)" : "var(--ink-muted)",
            },
            {
              // Saúde de envio da Meta: LIMITED/BLOCKED valem p/ conversas iniciadas
              // pela empresa (templates); responder lead na janela de 24h segue.
              label: "Envio (Meta)",
              value: metaStatus == null ? "Verificando…" : ({ AVAILABLE: "Liberado ✓", LIMITED: "Limitado", BLOCKED: "Bloqueado" } as Record<string, string>)[metaStatus.can_send ?? ""] ?? "Indisponível",
              color: metaStatus == null ? "var(--ink-muted)" : ({ AVAILABLE: "var(--ok)", LIMITED: "var(--warn)", BLOCKED: "var(--danger)" } as Record<string, string>)[metaStatus.can_send ?? ""] ?? "var(--ink-muted)",
            },
            {
              // 1.000 service messages entregues grátis/número/mês; esgotada sem
              // forma de pagamento = a Meta para de entregar texto livre.
              label: "Franquia de serviço do mês (Meta, aproximado)",
              value: metaStatus == null ? "Verificando…" : !metaStatus.free_tier ? "Indisponível"
                : metaStatus.free_tier.free_remaining > 0
                  ? `${metaStatus.free_tier.free_used.toLocaleString("pt-BR")} de ${metaStatus.free_tier.free_tier_limit.toLocaleString("pt-BR")} usadas · restam ${metaStatus.free_tier.free_remaining.toLocaleString("pt-BR")}`
                  : `Esgotada${metaStatus.free_tier.billable ? ` · ${metaStatus.free_tier.billable.toLocaleString("pt-BR")} cobradas` : ""}`,
              color: !metaStatus?.free_tier ? "var(--ink-muted)" : metaStatus.free_tier.free_remaining > 0 ? "var(--ink)" : metaStatus.payment_method === true ? "var(--warn)" : "var(--danger)",
              wide: true,
            },
          ].map(m => (
            <div key={m.label} style={{ background: "var(--surface-hover)", padding: "10px 12px", gridColumn: m.wide ? "1 / -1" : undefined }}>
              <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 3 }}>{m.label}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: m.color }}>{m.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Diagnóstico da Meta (health_status): pagamento, verificação do negócio etc. */}
      {conn.status === "active" && !!metaStatus?.health_errors?.length && (
        <div style={{ background: "var(--warn-soft)", border: "1px solid var(--warn-line)", borderRadius: 8, padding: "8px 12px", marginBottom: 16, display: "flex", flexDirection: "column", gap: 4 }}>
          {metaStatus.health_errors!.map((e, i) => (
            <span key={i} style={{ fontSize: 12, color: "var(--warn)" }}><AlertTriangleIcon size={12} /> {e}</span>
          ))}
          <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>Afeta conversas iniciadas pela empresa (templates). Responder leads na janela de 24h continua funcionando.</span>
        </div>
      )}

      {/* Subscribe falhou (coex/timeout) OU não confirmado (null): conexão ativa
          mas sem garantia de receber webhook — retry manual. Fail-closed. */}
      {conn.status === "active" && metaStatus != null && metaStatus.subscribed !== true && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--warn-soft)", border: "1px solid var(--warn-line)", borderRadius: 8, padding: "8px 12px", marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: "var(--warn)", flex: 1 }}>
            <AlertTriangleIcon size={12} /> App não inscrito nos webhooks da Meta — as mensagens não chegam ao agente.
            {resubMut.isError && <em style={{ color: "var(--danger)", fontStyle: "normal" }}> Retry falhou, tente novamente.</em>}
          </span>
          <button
            onClick={() => resubMut.mutate()}
            disabled={resubMut.isPending || metaChecking}
            style={{ background: "var(--warn)", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, padding: "5px 12px", cursor: "pointer", opacity: resubMut.isPending || metaChecking ? 0.6 : 1 }}
          >
            {resubMut.isPending ? "Inscrevendo…" : metaChecking ? "Conferindo…" : "Reinscrever"}
          </button>
        </div>
      )}

      {/* Coex que caiu de volta pro app (ON_PREMISE): /register não recupera
          (Meta recusa "not available for SMB businesses") — a saída é refazer
          o pareamento da coexistência no app do celular. */}
      {conn.status === "active" && metaStatus != null && metaStatus.registered !== true && metaStatus.platform_type === "ON_PREMISE" && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--warn-soft)", border: "1px solid var(--warn-line)", borderRadius: 8, padding: "8px 12px", marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: "var(--warn)", flex: 1 }}>
            <AlertTriangleIcon size={12} /> Coexistência desconectada — o número voltou a operar só pelo app WhatsApp Business
            (mensagens não chegam à plataforma). Refaça o pareamento no celular: app WhatsApp Business
            atualizado e online, repetindo a conexão da coexistência (QR code). O registro manual não resolve este caso.
          </span>
        </div>
      )}

      {/* Registro do número falhou (coex/timeout) OU não confirmado (null,
          check na Meta deu erro — token/id): envio sem garantia — retry manual */}
      {conn.status === "active" && metaStatus != null && metaStatus.registered !== true && metaStatus.platform_type !== "ON_PREMISE" && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--warn-soft)", border: "1px solid var(--warn-line)", borderRadius: 8, padding: "8px 12px", marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: "var(--warn)", flex: 1 }}>
            <AlertTriangleIcon size={12} />{" "}
            {metaStatus.registered === false
              ? "Número não registrado na Cloud API — o envio falha (erro 133010)."
              : "A Meta não respondeu o estado do número (token ou vínculo com problema) — trate como desconectado."}
            {registerMut.isError && (
              <em style={{ color: "var(--danger)", fontStyle: "normal" }}>
                {" "}{(registerMut.error as any)?.payload?.detail?.error ?? (registerMut.error as any)?.payload?.detail ?? "Retry falhou, tente novamente."}
              </em>
            )}
          </span>
          <button
            onClick={() => registerMut.mutate()}
            disabled={registerMut.isPending || metaChecking}
            style={{ background: "var(--warn)", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, padding: "5px 12px", cursor: "pointer", opacity: registerMut.isPending || metaChecking ? 0.6 : 1 }}
          >
            {registerMut.isPending ? "Registrando…" : metaChecking ? "Conferindo…" : "Registrar número"}
          </button>
        </div>
      )}

      {/* Ficha da WABA na Meta (verificação do negócio, revisão da conta, BM dona) */}
      {conn.status === "active" && !!conn.waba_id && (
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => wabaAccountMut.mutate()}
            disabled={wabaAccountMut.isPending}
            style={{ background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 5, color: "var(--ink-secondary)", fontSize: 11, padding: "3px 8px", cursor: "pointer", opacity: wabaAccountMut.isPending ? 0.6 : 1 }}
          >
            {wabaAccountMut.isPending ? "Consultando…" : "Dados da WABA (Meta)"}
          </button>
          {(wabaAccountMut.data || wabaAccountMut.isError) && (
            <pre style={{ marginTop: 8, padding: "8px 12px", background: "var(--surface-hover)", border: `1px solid ${wabaAccountMut.isError ? "var(--danger)" : "var(--line)"}`, borderRadius: 8, fontSize: 11, color: wabaAccountMut.isError ? "var(--danger)" : "var(--ink)", whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 320, overflow: "auto" }}>
              {JSON.stringify(wabaAccountMut.isError ? ((wabaAccountMut.error as any)?.payload?.detail ?? String(wabaAccountMut.error)) : wabaAccountMut.data, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Credenciais */}
      <div style={{ marginBottom: 14 }}>
        <div style={labelStyle}>Instance ID</div>
        <div style={rowStyle}>
          <code style={codeStyle}>{conn.instance_id}</code>
          <CopyButton value={conn.instance_id} />
        </div>
        <div style={labelStyle}>API Key</div>
        <div style={rowStyle}>
          <code style={{ ...codeStyle, letterSpacing: revealed ? "normal" : "0.1em" }}>
            {revealed && apiKey ? apiKey : "••••••••••••••••••••••••"}
          </code>
          <button onClick={handleReveal} style={{ background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 5, color: "var(--ink-secondary)", fontSize: 11, padding: "3px 8px", cursor: "pointer" }}>
            {revealed ? "Ocultar" : "Revelar"}
          </button>
          {revealed && apiKey && <CopyButton value={apiKey} />}
          <button onClick={handleRotateKey} title="Gera uma chave nova e desliga a atual na hora" style={{ background: "transparent", border: "1px solid var(--danger)", borderRadius: 5, color: "var(--danger)", fontSize: 11, padding: "3px 8px", cursor: "pointer" }}>
            Gerar nova
          </button>
        </div>
      </div>

      {/* Agente vinculado — uma WABA tem no máximo um agente */}
      <div style={{ marginBottom: 14 }}>
        <div style={labelStyle}>Agente vinculado</div>
        {conn.claimed_by_agent_id ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 4, padding: "7px 12px", background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 8 }}>
            <span style={{ fontSize: 13, color: "var(--ink)" }}>{conn.claimed_by_agent_name ?? "(removido)"}</span>
            <button
              title="Desvincular do agente"
              onClick={() => setConfirmUnbind(true)}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", background: "var(--danger-soft)", border: "1px solid var(--danger)", borderRadius: 6, color: "var(--danger)", cursor: "pointer", padding: "4px 6px", lineHeight: 1 }}
            >
              <XIcon size={14} />
            </button>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 4 }}>Sem agente vinculado</div>
        )}
      </div>
      {confirmUnbind && (
        <ConfirmDialog
          title="Desvincular agente"
          message={`Desvincular "${conn.claimed_by_agent_name ?? "agente removido"}" de ${conn.name}? O número continua ativo; o agente para de receber as mensagens.`}
          confirmLabel="Desvincular"
          danger
          onConfirm={() => { unbindMut.mutate(); setConfirmUnbind(false); }}
          onCancel={() => setConfirmUnbind(false)}
        />
      )}

      {/* Conexão externa: sem canais Meta nem toggles — só entrada, webhook de
          saída e o caminho de migração pro WhatsApp oficial. */}
      {conn.external ? (
        <ExternalConnection conn={conn} onChanged={onChanged} />
      ) : (
      <>
      {/* Canais — a conexão é do cliente, não do canal: cada um conecta e se
          configura de forma independente. */}
      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 14, marginBottom: 14, display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Conexão zerada: oferece o login único (Embedded Signup v4 completo)
            antes dos canais individuais. Some assim que qualquer canal liga. */}
        {!conn.phone_number_id && !conn.ig_user_id && !conn.messenger_active && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <CompleteSignup connectionId={conn.id} onSuccess={onChanged} />
            <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>
              Um login só conecta os três canais — ou conecte cada um separadamente abaixo.
            </span>
          </div>
        )}
        <ChannelSection
          nome="WhatsApp"
          conectado={!!conn.phone_number_id && !conn.meta_disconnected_at}
          detalhe={conn.phone_number}
          bm={conn.waba_business_name}
          aberto={isOpen("whatsapp")}
          onToggle={() => toggleChannel("whatsapp")}
        >
          {conn.phone_number_id && conn.meta_disconnected_at ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "var(--danger-soft)", border: "1px solid var(--danger)", borderRadius: 8, padding: "8px 12px" }}>
                <span style={{ display: "inline-flex", lineHeight: 1, color: "var(--danger)", marginTop: 1 }}><AlertTriangleIcon size={12} /></span>
                <span style={{ fontSize: 12, color: "var(--danger)" }}>
                  Desconectada na Meta desde{" "}
                  {/* utcnow() grava UTC naive (ISO sem Z) — sem o sufixo o Date interpreta como hora local */}
                  {new Date(conn.meta_disconnected_at.endsWith("Z") || conn.meta_disconnected_at.includes("+") ? conn.meta_disconnected_at : conn.meta_disconnected_at + "Z")
                    .toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" })}.
                  {" "}Reconecte com o <strong>mesmo número</strong> ({conn.phone_number}) — leads e configurações são preservados.
                </span>
              </div>
              <EmbeddedSignup connectionId={conn.id} onSuccess={onChanged} />
            </div>
          ) : conn.phone_number_id ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <SendBehaviorToggles conn={conn} onChanged={onChanged} />
              <button
                onClick={async () => {
                  if (!confirm(`Desconectar o WhatsApp ${conn.phone_number ?? ""}? A conexão fica marcada como desconectada e o botão de reconexão é liberado. Leads, API key e vínculo de agente são preservados.`)) return;
                  await wabaApi.post(`/connections/${conn.id}/disconnect`);
                  await onChanged();
                }}
                style={{
                  alignSelf: "flex-start", padding: "5px 12px", borderRadius: 6,
                  border: "1px solid var(--line-strong)", background: "transparent",
                  color: "var(--danger)", fontSize: 12, cursor: "pointer",
                }}
              >
                Desconectar
              </button>
            </div>
          ) : (
            <EmbeddedSignup connectionId={conn.id} onSuccess={onChanged} />
          )}
        </ChannelSection>

        <ChannelSection
          nome="Instagram"
          conectado={!!conn.ig_user_id}
          detalhe={conn.ig_username ? `@${conn.ig_username}` : null}
          bm={conn.page_business_name}
          saude={conn.ig_user_id ? igSaude : null}
          aberto={isOpen("instagram")}
          onToggle={() => toggleChannel("instagram")}
        >
          {conn.ig_user_id ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {igResubBox}
              <div>
                <div style={{ fontSize: 11, color: "var(--ink-muted)", marginBottom: 6 }}>
                  Resposta pública a comentários
                </div>
                <IgCommentReply conn={conn} onChanged={onChanged} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--ink-muted)", marginBottom: 6 }}>
                  Comportamento de envio
                </div>
                <SendBehaviorToggles conn={conn} onChanged={onChanged} channel="instagram" />
              </div>
              <button
                onClick={async () => {
                  if (!confirm(`Desconectar @${conn.ig_username} desta conexão? O WhatsApp não é afetado e o texto da resposta a comentários é preservado.`)) return;
                  await wabaApi.delete(`/connections/${conn.id}/instagram`);
                  await onChanged();
                }}
                style={{
                  alignSelf: "flex-start", padding: "5px 12px", borderRadius: 6,
                  border: "1px solid var(--line-strong)", background: "transparent",
                  color: "var(--danger)", fontSize: 12, cursor: "pointer",
                }}
              >
                Desconectar Instagram
              </button>
            </div>
          ) : (
            <InstagramSignup connectionId={conn.id} onSuccess={onChanged} />
          )}
        </ChannelSection>

        <ChannelSection
          nome="Messenger"
          conectado={!!conn.messenger_active}
          detalhe={conn.messenger_active ? conn.page_name : null}
          bm={conn.page_business_name}
          saude={conn.messenger_active ? igSaude : null}
          aberto={isOpen("messenger")}
          onToggle={() => toggleChannel("messenger")}
        >
          {conn.messenger_active ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {igResubBox}
              <div style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 }}>
                Mensagens do Messenger da Página — incluindo o chat do Marketplace —
                chegam nesta conexão.
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--ink-muted)", marginBottom: 6 }}>
                  Comportamento de envio
                </div>
                <SendBehaviorToggles conn={conn} onChanged={onChanged} channel="messenger" />
              </div>
              <button
                onClick={async () => {
                  if (!confirm(`Desconectar o Messenger de ${conn.page_name || "esta Página"}? WhatsApp e Instagram não são afetados.`)) return;
                  await wabaApi.delete(`/connections/${conn.id}/messenger`);
                  await onChanged();
                }}
                style={{
                  alignSelf: "flex-start", padding: "5px 12px", borderRadius: 6,
                  border: "1px solid var(--line-strong)", background: "transparent",
                  color: "var(--danger)", fontSize: 12, cursor: "pointer",
                }}
              >
                Desconectar Messenger
              </button>
            </div>
          ) : (
            <MessengerSignup connectionId={conn.id} onSuccess={onChanged} />
          )}
        </ChannelSection>

        <ChannelSection
          nome="Anúncios"
          conectado={!!conn.ads_connected}
          detalhe={conn.ads_connected ? "Marketing API" : null}
          aberto={isOpen("ads")}
          onToggle={() => toggleChannel("ads")}
        >
          {conn.ads_connected ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 }}>
                Leitura de contas e insights de anúncios pela API (GET /ads/accounts e /ads/insights).
                Eventos de conversão (POST /ads/events) usam o WhatsApp, não este vínculo.
              </div>
              <button
                onClick={async () => {
                  if (!confirm("Desconectar os anúncios desta conexão? Os canais não são afetados.")) return;
                  await wabaApi.delete(`/connections/${conn.id}/ads`);
                  await onChanged();
                }}
                style={{
                  alignSelf: "flex-start", padding: "5px 12px", borderRadius: 6,
                  border: "1px solid var(--line-strong)", background: "transparent",
                  color: "var(--danger)", fontSize: 12, cursor: "pointer",
                }}
              >
                Desconectar anúncios
              </button>
            </div>
          ) : (
            <AdsSignup connectionId={conn.id} onSuccess={onChanged} />
          )}
        </ChannelSection>

        <ChannelSection
          nome="Mercado Livre"
          conectado={conn.meli_status === "connected"}
          saude={conn.meli_status === "reconnect" ? { texto: "Reconectar", cor: "var(--danger)" } : null}
          detalhe={conn.meli_user_id ? conn.meli_nickname || conn.meli_user_id : null}
          aberto={isOpen("meli")}
          onToggle={() => toggleChannel("meli")}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 }}>
              Cada clique em WhatsApp, Ligar ou pedido de contato nos anúncios de veículo da loja chega
              como lead (canal Mercado Livre) nos webhooks v3 desta conexão. Sem IA.
            </div>
            {conn.meli_status === "reconnect" && (
              <div style={{ fontSize: 12, color: "var(--danger)", lineHeight: 1.5 }}>
                O Mercado Livre derrubou o acesso (senha trocada, autorização revogada ou muito tempo sem uso).
                Mande um link novo para o lojista autorizar de novo.
              </div>
            )}
            {conn.meli_status !== "connected" && <ClassificadoConnect connectionId={conn.id} provider="meli" />}
            {conn.meli_user_id && <MeliDiagnostico connectionId={conn.id} />}
            {conn.meli_user_id && <IntegracaoLog connectionId={conn.id} canal="mercadolivre" />}
            {conn.meli_user_id && (
              <button
                onClick={async () => {
                  if (!confirm(`Desconectar o Mercado Livre (${conn.meli_nickname || conn.meli_user_id}) desta conexão? Os outros canais não são afetados.`)) return;
                  await wabaApi.delete(`/connections/${conn.id}/meli`);
                  await onChanged();
                }}
                style={{
                  alignSelf: "flex-start", padding: "5px 12px", borderRadius: 6,
                  border: "1px solid var(--line-strong)", background: "transparent",
                  color: "var(--danger)", fontSize: 12, cursor: "pointer",
                }}
              >
                Desconectar Mercado Livre
              </button>
            )}
          </div>
        </ChannelSection>

        <ChannelSection
          nome="OLX"
          conectado={conn.olx_status === "connected"}
          saude={conn.olx_status === "reconnect" ? { texto: "Reconectar", cor: "var(--danger)" } : null}
          detalhe={conn.olx_status ? conn.olx_account_name || conn.olx_account_email || "conta conectada" : null}
          aberto={isOpen("olx")}
          onToggle={() => toggleChannel("olx")}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 }}>
              Mensagens do chat e leads (WhatsApp, Ligar, financiamento) dos anúncios da loja chegam como lead
              (canal OLX) nos webhooks v3 desta conexão; a resposta vai pelo /send/olx. Sem IA. A OLX entrega o
              chat para um sistema só: se a loja usa outro CRM no chat da OLX, ele para de receber.
            </div>
            {conn.olx_status === "reconnect" && (
              <div style={{ fontSize: 12, color: "var(--danger)", lineHeight: 1.5 }}>
                A OLX recusou o acesso (autorização revogada ou senha trocada). Mande um link novo para o lojista
                autorizar de novo.
              </div>
            )}
            {conn.olx_status !== "connected" && <ClassificadoConnect connectionId={conn.id} provider="olx" />}
            {conn.olx_status && <OlxLeadsConfig connectionId={conn.id} />}
            {conn.olx_status && <IntegracaoLog connectionId={conn.id} canal="olx" />}
            {conn.olx_status && (
              <button
                onClick={async () => {
                  if (!confirm(`Desconectar a OLX (${conn.olx_account_name || conn.olx_account_email || "conta"}) desta conexão? O chat e os leads da OLX param de chegar; os outros canais não são afetados.`)) return;
                  await wabaApi.delete(`/connections/${conn.id}/olx`);
                  await onChanged();
                }}
                style={{
                  alignSelf: "flex-start", padding: "5px 12px", borderRadius: 6,
                  border: "1px solid var(--line-strong)", background: "transparent",
                  color: "var(--danger)", fontSize: 12, cursor: "pointer",
                }}
              >
                Desconectar OLX
              </button>
            )}
          </div>
        </ChannelSection>

        <ChannelSection
          nome="Webmotors"
          conectado={wmOk}
          detalhe={conn.webmotors_user ?? null}
          aberto={isOpen("webmotors")}
          onToggle={() => toggleChannel("webmotors")}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 }}>
              Propostas e cliques em Ligar ou WhatsApp nos anúncios da loja chegam como lead (canal Webmotors) nos
              webhooks v3 desta conexão, com a carga completa da Webmotors. Sem IA. Os leads são aceitos
              automaticamente: a loja não consegue mais recusá-los no Cockpit.
            </div>
            {conn.webmotors_user && (
              <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                {conn.webmotors_cnpj || conn.webmotors_cliente_id
                  ? `Loja ${conn.webmotors_cliente_id ?? "—"}${conn.webmotors_cnpj ? ` · CNPJ ${conn.webmotors_cnpj}` : ""}`
                  : "Loja ainda não identificada (aguardando o 1º lead)."}
              </div>
            )}
            {conn.webmotors_user && !wmOk && (
              <div style={{ fontSize: 12, color: "var(--danger)", lineHeight: 1.5 }}>
                {conn.webmotors_status === "reconnect"
                  ? "A Webmotors recusou o e-mail/senha do Integrador de API (senha trocada ou usuário removido). Mande um link novo para o lojista."
                  : `${conn.webmotors_error ?? "Sem resposta da Webmotors"}${wmOkAt ? ` · última consulta que deu certo: ${spTime(wmOkAt)}` : ""}`}
              </div>
            )}
            {(!conn.webmotors_user || conn.webmotors_status === "reconnect") && (
              <ClassificadoConnect connectionId={conn.id} provider="webmotors" />
            )}
            {/* sempre: a trilha também guarda as tentativas da página do lojista (antes de conectar) */}
            <IntegracaoLog connectionId={conn.id} canal="webmotors" />
            {conn.webmotors_user && (
              <button
                onClick={async () => {
                  if (!confirm(`Desconectar a Webmotors (${conn.webmotors_user}) desta conexão? Os leads param de chegar aqui; para parar de vez, a loja desativa o app em Cockpit → CRM → Integrações. Os outros canais não são afetados.`)) return;
                  await wabaApi.delete(`/connections/${conn.id}/webmotors`);
                  await onChanged();
                }}
                style={{
                  alignSelf: "flex-start", padding: "5px 12px", borderRadius: 6,
                  border: "1px solid var(--line-strong)", background: "transparent",
                  color: "var(--danger)", fontSize: 12, cursor: "pointer",
                }}
              >
                Desconectar Webmotors
              </button>
            )}
          </div>
        </ChannelSection>

        <ChannelSection
          nome="E-mail"
          conectado={!!conn.email_address}
          detalhe={conn.email_address ?? null}
          saude={emailSaude}
          aberto={isOpen("email")}
          onToggle={() => toggleChannel("email")}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 }}>
              Leads que os portais mandam por e-mail (iCarros, Mobiauto, Autoline, Chaves na Mão, Na Pista, Usados BR…)
              chegam como mensagem (canal E-mail) nos webhooks v3 desta conexão, com o e-mail completo. Sem IA e sem
              ler a caixa de ninguém: a loja cola o endereço abaixo no portal, no campo de e-mail para receber propostas.
            </div>
            {conn.email_address ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <code style={{
                  fontSize: 13, padding: "6px 10px", borderRadius: 6, userSelect: "all",
                  background: "var(--surface-input)", border: "1px solid var(--line)", color: "var(--ink)",
                }}>
                  {conn.email_address}
                </code>
                <CopyButton value={conn.email_address} />
              </div>
            ) : (
              <button
                onClick={async (e) => {
                  (e.currentTarget as HTMLButtonElement).disabled = true; // duplo clique: 1 endereço só
                  try { await wabaApi.post(`/connections/${conn.id}/email`); } finally { await onChanged(); }
                }}
                style={{
                  alignSelf: "flex-start", padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: "none", background: "var(--brand)", color: "var(--surface-card)", cursor: "pointer",
                }}
              >
                Criar e-mail
              </button>
            )}
            <IntegracaoLog connectionId={conn.id} canal="email" />
            {conn.email_address && (
              <button
                onClick={async () => {
                  if (!confirm(`Desativar o e-mail ${conn.email_address}? O que os portais ainda mandarem para ele será ignorado; "Criar e-mail" gera um endereço novo. Os outros canais não são afetados.`)) return;
                  await wabaApi.delete(`/connections/${conn.id}/email`);
                  await onChanged();
                }}
                style={{
                  alignSelf: "flex-start", padding: "5px 12px", borderRadius: 6,
                  border: "1px solid var(--line-strong)", background: "transparent",
                  color: "var(--danger)", fontSize: 12, cursor: "pointer",
                }}
              >
                Desativar e-mail
              </button>
            )}
          </div>
        </ChannelSection>

      </div>
      </>
      )}

      {/* Ferramentas (active) — Desativar/Reativar/Remover moraram aqui; hoje ficam no header */}
      {conn.status === "active" && (
        <div style={{ display: "flex", gap: 8, borderTop: "1px solid var(--line)", paddingTop: 14, flexWrap: "wrap" }}>
          {conn.status === "active" && (
            <>
              <button style={{ flex: 1, background: "transparent", border: "1px solid var(--line-bold)", borderRadius: 8, color: showWebhooks ? "var(--ink)" : "var(--ink-secondary)", fontSize: 12, padding: "7px 0", cursor: "pointer" }}
                onClick={() => setShowWebhooks(true)}>
                Webhooks ({webhooks.length}/5)
              </button>
              <button style={{ flex: 1, background: "transparent", border: "1px solid var(--line-bold)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 12, padding: "7px 0", cursor: "pointer" }}
                onClick={() => setShowLog(true)}>
                Log
              </button>
              <button style={{ flex: 1, background: "transparent", border: "1px solid var(--line-bold)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 12, padding: "7px 0", cursor: "pointer" }}
                onClick={() => setShowLeads(true)}>
                Leads
              </button>
              {conn.phone_number_id && (
                <button style={{ flex: 1, background: "transparent", border: "1px solid var(--line-bold)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 12, padding: "7px 0", cursor: "pointer" }}
                  onClick={() => setShowCalls(true)}>
                  Chamadas
                </button>
              )}
              {!conn.external && (
                <button style={{ flex: 1, background: "transparent", border: "1px solid var(--line-bold)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 12, padding: "7px 0", cursor: "pointer" }}
                  onClick={() => setShowTemplates(true)}>
                  Templates
                </button>
              )}
              {(canManageParticipants || canChangeOwner) && (
                <button
                  style={{ flex: 1, background: "transparent", border: "1px solid var(--line-bold)", borderRadius: 8, color: accessMode !== null ? "var(--ink)" : "var(--ink-secondary)", fontSize: 12, padding: "7px 0", cursor: "pointer" }}
                  onClick={() => {
                    if (canManageParticipants && canChangeOwner) setAccessMode("choose");
                    else if (canManageParticipants) setAccessMode("participants");
                    else setAccessMode("transfer-search");
                  }}
                >
                  Acessos
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Popup de outbound webhooks */}
      {conn.status === "active" && showWebhooks && (
        <div {...backdropClose(() => { setShowWebhooks(false); })} style={modalBase}>
          <DialogPanel onClose={() => setShowWebhooks(false)} label="Webhooks" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 20, width: 560, maxWidth: "92vw", maxHeight: "85vh", overflowY: "auto" }}>
            <OutboundWebhooks connectionId={conn.id} />
          </DialogPanel>
        </div>
      )}

      {/* Modal de log */}
      {showLog && (
        <MessageLog connectionId={conn.id} initialChannel={initialLog ?? undefined} onClose={() => setShowLog(false)} />
      )}

      {/* Softphone da Calling API (dash de teste) */}
      {showCalls && (
        <CallConsole connectionId={conn.id} instanceId={conn.instance_id} onClose={() => setShowCalls(false)} />
      )}

      {/* Modal de busca de leads */}
      {showLeads && (
        <LeadSearch connectionId={conn.id} instanceId={conn.instance_id} external={!!conn.external} onClose={() => setShowLeads(false)} />
      )}

      {/* Modal de templates */}
      {showTemplates && (
        <TemplateManager connectionId={conn.id} canCreate={canCreateTemplate} onClose={() => setShowTemplates(false)} />
      )}

    </div>
    </>
  );
}
